import { atom, getDefaultStore } from 'jotai';
import { deepEqual } from 'fast-equals';
import { v4 as uuid } from 'uuid';
import { selectAtom, unwrap } from 'jotai/utils';
import { focusAtom } from 'jotai-optics';
import {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreEnumsPricerStatus,
  BrokerNerdsBarkerCoreModelsInventoryDtiSplitRules,
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponse,
  BrokerNerdsBarkerCoreModelsInventoryListing,
  BrokerNerdsBarkerCoreModelsMarketplacesListing,
  GetApiInventoryParams,
  getGetApiPricingRulesRuleIdQueryOptions,
  getApiMarketplacesMarketplaceIdEventsEventIdListings,
  getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey,
} from '../api';
import {
  BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponseExtended,
  BarkerEventListing,
  FilterMode,
  GlobalTransientState,
  ListingUpdate,
  MarketplaceEventExtended,
  MarketplacesListingWithAlias,
  Rule,
} from '../types';
import { SetStateAction } from 'react';
import { weakEquals } from '../utils/weakEquals';
import { calculateTargetPrice, getRowComparisonValue, getSplitsValue } from '../utils/rule-utils';
import { getOutlierListings, getOutlierPriceThreshold, GetTargetComparablePrice, GetTargetComparables } from '../hoc/MarketListings/marketListingsFilter';
import { DEFAULT_FILTERS, DEFAULT_RULE, DEFAULT_RULE_SETTINGS } from './constants';
import { convertSplitsModelToPointOfSaleModel, getPointOfSaleSplitType } from '../components/Splits/Splits.utils';
import { TenantIdEventId, TenantIdListingId } from '../models/tenantIdListingId';
import { StringWithPipe } from '../ts-utils';
import dayjs from 'dayjs';
import { auth } from './atoms.auth';
import { atomWithQuery } from 'jotai-tanstack-query';
import { marketListingsLimit } from './marketListingsLimit';
import { Sale } from '../hoc/Sales/Sales.types';
import { getDefaultMarketplaceForTenant } from '../utils/marketplace-utils';
import { normalizePricingRuleFilters } from '../utils/pricing-rule-filters';
import { applyFiltersAsync } from '../workers/marketListingsFilterClient';
import { atomFamily } from 'jotai-family';

export const store = getDefaultStore();
window.atomStore = store;
// @ts-ignore - dev_get_mounted_atoms is not typed in jotai's Store, but it exists and is used for debugging
window.atomStoreGetValue = (label: string) => (store.dev_get_mounted_atoms ? store.get([...store.dev_get_mounted_atoms()].find((x) => x.debugLabel === label)!) : null);

export namespace pricing {
  //TODO: Begin moving all the atoms into pricing namespace to avoid collisions and poluting global namespace
}
export const sectionSuggestionsAtom = atom<{ tenantIdListingId: TenantIdListingId | null; suggestedSections: string[] }>({ tenantIdListingId: null, suggestedSections: [] });
export const searchParamsHistoryAtom = atom<GetApiInventoryParams | null>(null);
export const selectedSearchEventsAtom = atom<BrokerNerdsBarkerCoreModelsInventoryEvent[]>([]);
export const hasSearchResultsAtom = atom<boolean>(false);
const _searchResultsAtom = atom<BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponseExtended | null>(null);
export const searchResultsAtom = atom(
  (get) => get(_searchResultsAtom),
  (get, set, value: BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponseExtended | null) => {
    const selectedListing = get(selectedMergedListingAtom);
    const found = value?.listings.find((x) => x.tenantId === selectedListing?.tenantId && x.listingId === selectedListing?.listingId);
    if (!found) {
      set(selectedTenantListingIdAtom, null);
    }
    set(_searchResultsAtom, value);
  },
);

export const rowDraggingAtom = atom(false);
export const initialPageLoadAtom = atom(true);

const _showBulkOptionsAtom = atom(false);
export const showBulkOptionsAtom = atom(
  (get) => get(_showBulkOptionsAtom),
  (get, set, show: boolean | SetStateAction<boolean>) => {
    const value = typeof show === 'function' ? show(get(_showBulkOptionsAtom)) : show;
    set(_showBulkOptionsAtom, value);
    if (!value) {
      set(_markListingsAtom, []);
    }
  },
);

const _markListingsAtom = atom<string[]>([]);
export const markListingsAtom = atom(
  (get) => get(_markListingsAtom),
  (get, set, tenantIdlistingId: string | string[] | SetStateAction<string[]>) => {
    if (Array.isArray(tenantIdlistingId)) {
      // Single set with the final value; setting the unfiltered array first only caused an extra render.
      set(
        _markListingsAtom,
        tenantIdlistingId.filter((l) => !l.endsWith('sales')),
      );
    } else if (typeof tenantIdlistingId === 'string') {
      const exists = get(_markListingsAtom).includes(tenantIdlistingId);
      if (exists) {
        set(_markListingsAtom, [...get(_markListingsAtom).filter((x) => x !== tenantIdlistingId)]);
      } else {
        set(_markListingsAtom, [...new Set([...get(_markListingsAtom), tenantIdlistingId])]);
      }
    } else if (typeof tenantIdlistingId === 'function') {
      set(_markListingsAtom, [...new Set([...tenantIdlistingId(get(_markListingsAtom).filter((l) => !l.endsWith('sales')))])]);
    }
  },
);

export const filterOutListingsAtom = atom<string[]>([]);

export const mergedEventListingsUnfilteredRulesAtom = atom<BarkerEventListing[]>((get) => {
  const search = get(searchResultsAtom);
  const eventsMap = new Map(search?.events.map((event) => [TenantIdEventId.create(event.tenantId, event.eventId).toString(), event]));
  const filterOutListings = new Set(get(filterOutListingsAtom));
  const selectedSearchEvent = get(selectedSearchEventsAtom);
  const selectedEventKeys = new Set(selectedSearchEvent.map((event) => `${event.tenantId}|${event.eventId}`));

  // Precompute listing count per ruleId once. Was a `search.listings.filter(...)` per listing => O(n^2).
  const ruleCounts = new Map<string, number>();
  for (const l of search?.listings ?? []) {
    if (l.ruleId) {
      ruleCounts.set(l.ruleId, (ruleCounts.get(l.ruleId) ?? 0) + 1);
    }
  }

  const listings =
    search?.listings
      .filter((listing) => selectedEventKeys.has(`${listing.tenantId}|${listing.eventId}`))
      // .filter((listing) => (selectedSearchEvent.length ? selectedSearchEvent.some((event) => event.tenantId === listing.tenantId && event.eventId === listing.eventId) : true))
      .map((listing) => {
        const eventKey = `${listing.tenantId}|${listing.eventId}`;
        const event = eventsMap.get(eventKey)!;
        const count = listing.ruleId ? (ruleCounts.get(listing.ruleId) ?? 0) : 0;
        return {
          ...listing,
          event,
          ruleCount: count,
          tenantIdEventId: eventKey as StringWithPipe,
          tenantIdListingId: TenantIdListingId.create(listing.tenantId, listing.listingId).toStringTyped(),
          tags: listing.tags ?? [],
        } satisfies BarkerEventListing;
      })
      .filter((x) => !filterOutListings.has(x.tenantIdListingId)) || [];

  // Add fake listing for each uniqie tenantId+eventId combination
  const fakeListings = Array.from(eventsMap)
    .map(
      ([, event]) =>
        ({
          eventId: event.eventId,
          event,
          tenantId: event.tenantId,
          ruleId: null,
          listingId: `${event.eventId}|sales`,
          tenantIdEventId: TenantIdEventId.create(event.tenantId, event.eventId).toStringTyped(),
          tenantIdListingId: `${event.tenantId}|${event.eventId}|sales` as StringWithPipe,
          isBroadcasting: false,
          pricerStatusId: 'None',
          quantityRemaining: 0,
          row: 'Sales',
          section: 'Sales',
          seatFrom: '0',
          seatThru: '0',
          unitCost: 0,
          ruleCount: 0,
          tags: [],
          unitPrice: 0,
          quantityReserved: 0,
          seatingType: 'Unknown',
          ticketStockId: 'Unknown',
          numBarcodes: 0,
          unitFace: 0,
          cachedAt: new Date(2001, 0, 1),
        }) satisfies BarkerEventListing,
    )
    .filter((x) => selectedEventKeys.has(`${x.tenantId}|${x.eventId}`));
  // .filter((x) => (selectedSearchEvent.length ? selectedSearchEvent.some((y) => y.tenantId === x.tenantId && y.eventId === x.eventId) : true));
  listings.push(...fakeListings);

  return listings;
});

export const mergedEventListingsAtom = atom<BarkerEventListing[]>((get) => {
  // Sorted by ruleId then ruleTier asc, so the first row seen for a ruleId is its lowest tier.
  // Dedupe with a Set instead of acc.find(...) (which was O(n^2)).
  const sorted = get(mergedEventListingsUnfilteredRulesAtom)
    .filter((x) => x.ruleId)
    .sort((a, b) => {
      if (a.ruleId === b.ruleId) {
        return (a.ruleTier || 0) - (b.ruleTier || 0);
      }
      return a.ruleId!.localeCompare(b.ruleId!);
    });
  const seenRuleIds = new Set<string>();
  const listingsWithLowestRuleTier = new Set<string>();
  for (const listing of sorted) {
    if (!seenRuleIds.has(listing.ruleId!)) {
      seenRuleIds.add(listing.ruleId!);
      listingsWithLowestRuleTier.add(listing.tenantIdListingId);
    }
  }
  return get(mergedEventListingsUnfilteredRulesAtom).filter((x) => x.ruleId === null || listingsWithLowestRuleTier.has(x.tenantIdListingId));
});

export const updateEventAtom = atom(null, (get, set, event: BrokerNerdsBarkerCoreModelsInventoryEvent) => {
  const searchResults = get(searchResultsAtom);
  const index = searchResults?.events.findIndex((x) => x.eventId === event.eventId && x.tenantId === event.tenantId) ?? -1;
  if (searchResults && index > -1) {
    // Copy-on-write: only the events array and the one changed event get new references; listings are untouched.
    const events = [...searchResults.events];
    events[index] = event;
    // Hack to keep scrolling position when updating event from comments.
    set(transientGlobalStateAtom, (s) => ({ ...s, ignoreGridScrolling: true }));
    set(searchResultsAtom, { ...searchResults, events });
  }
});

export const updateEventListingsAtom = atom(
  null,
  (
    get,
    set,
    data: BrokerNerdsBarkerCoreModelsInventoryGetInventoryResponse & {
      tenantId: string;
      eventId: string;
    },
  ) => {
    const searchResults = get(searchResultsAtom) ?? { events: [], listings: [] };
    const { tenantId, eventId } = data;
    if (eventId) {
      if (data.events.length !== 0) {
        const event = data.events.find((e) => e.tenantId === tenantId && e.eventId === eventId);
        const previousEventListingKeys = new Set(searchResults.listings.filter((x) => x.tenantId === tenantId && x.eventId === eventId).map((x) => `${x.tenantId}|${x.listingId}`));
        const newListings = data.listings.map((x) => ({
          ...x,
          meta: { newRow: !previousEventListingKeys.has(`${x.tenantId}|${x.listingId}`) },
        }));
        const filteredOutNewListingsFromExisting = searchResults.listings.filter((x) => !(x.tenantId === tenantId && x.eventId === eventId));
        const mergedNewListings = [...filteredOutNewListingsFromExisting, ...newListings];

        // Copy-on-write: filter() already returns a fresh array; the other events keep their references.
        const events = searchResults.events.filter((e) => !(e.tenantId === tenantId && e.eventId === eventId));
        if (event) {
          events.push(event);
        }

        set(searchResultsAtom, {
          events,
          listings: mergedNewListings,
        });
      } else {
        // Event has no more listings: drop its listings and zero its counts. Previously this mutated the
        // atom's state object in place and never called set(), so subscribers were never notified.
        const listings = searchResults.listings.filter((x) => !(x.tenantId === tenantId && x.eventId === eventId));
        const events = [...searchResults.events];
        const eventIndex = events.findIndex((x) => tenantId === x.tenantId && eventId === x.eventId);
        if (eventIndex > -1) {
          events[eventIndex] = {
            ...events[eventIndex],
            openListings: 0,
            openTickets: 0,
          };
        }
        set(searchResultsAtom, { events, listings });
      }
    }
  },
);

function updateListing(
  listing: BrokerNerdsBarkerCoreModelsInventoryListing,
  index: number,
  listings: BrokerNerdsBarkerCoreModelsInventoryListing[],
  events: BrokerNerdsBarkerCoreModelsInventoryEvent[],
) {
  const oldListing = listings[index];
  const qtyDelta = listing.quantityRemaining - oldListing.quantityRemaining;
  const costDelta = listing.quantityRemaining * listing.unitCost - oldListing.unitCost * oldListing.quantityRemaining;
  // eslint-disable-next-line no-param-reassign
  listings[index] = listing;
  const eventIndex = events.findIndex((x) => x.tenantId === listing.tenantId && x.eventId === listing.eventId);
  if (eventIndex > -1) {
    const event = events[eventIndex];
    // eslint-disable-next-line no-param-reassign
    events[eventIndex] = {
      ...event,
      openTickets: event.openTickets + qtyDelta,
      openCost: event.openCost + costDelta,
    };
  }
}

function addListing(
  listing: BrokerNerdsBarkerCoreModelsInventoryListing,
  listings: BrokerNerdsBarkerCoreModelsInventoryListing[],
  events: BrokerNerdsBarkerCoreModelsInventoryEvent[],
) {
  listings.push(listing);
  const eventIndex = events.findIndex((x) => x.tenantId === listing.tenantId && x.eventId === listing.eventId);
  //NOTE:  Event may not be in list anymore.
  if (eventIndex > -1) {
    const event = events[eventIndex];
    // eslint-disable-next-line no-param-reassign
    events[eventIndex] = {
      ...event,
      openListings: event.openListings + 1,
      openTickets: event.openTickets + listing.quantityRemaining,
      openCost: event.openCost + listing.unitCost * listing.quantityRemaining,
    };
  }
}

function removeListing(
  listing: BrokerNerdsBarkerCoreModelsInventoryListing,
  listings: BrokerNerdsBarkerCoreModelsInventoryListing[],
  events: BrokerNerdsBarkerCoreModelsInventoryEvent[],
) {
  const index = listings.findIndex((x) => x.tenantId === listing.tenantId && x.listingId === listing.listingId);
  if (index > -1) {
    const eventIndex = events.findIndex((x) => x.tenantId === listing.tenantId && x.eventId === listing.eventId);
    if (eventIndex > -1) {
      const event = events[eventIndex];
      // eslint-disable-next-line no-param-reassign
      events[eventIndex] = {
        ...event,
        openListings: event.openListings - 1,
        openTickets: event.openTickets - listing.quantityRemaining,
        openCost: event.openCost - listing.unitCost * listing.quantityRemaining,
      };
      listings.splice(index, 1);
    }
  }
}

export const updateListingsAtom = atom(
  null,
  (
    get,
    set,
    listing:
      | BrokerNerdsBarkerCoreModelsInventoryListing
      | BrokerNerdsBarkerCoreModelsInventoryListing[]
      | { listingsToUpdate: BrokerNerdsBarkerCoreModelsInventoryListing[]; listingsToRemove?: TenantIdListingId[]; newSelectedListingId?: StringWithPipe },
  ) => {
    const data = get(searchResultsAtom);
    if (data) {
      // Copy-on-write: shallow-copy the arrays once (was a full structuredClone of every event + listing).
      // The helpers below only replace whole elements / push / splice on these copies, so existing
      // listing/event objects keep their references unless they actually change.
      const events = [...data.events];
      const listings = [...data.listings];
      // Build a key -> index map so update-or-add is O(1) per listing instead of findIndex O(n) per listing.
      const makeUpsert = () => {
        const indexByKey = new Map<string, number>();
        listings.forEach((l, i) => indexByKey.set(`${l.tenantId}|${l.listingId}`, i));
        return (l: BrokerNerdsBarkerCoreModelsInventoryListing) => {
          const key = `${l.tenantId}|${l.listingId}`;
          const index = indexByKey.get(key);
          if (index !== undefined) {
            updateListing(l, index, listings, events);
          } else {
            addListing(l, listings, events);
            indexByKey.set(key, listings.length - 1);
          }
        };
      };
      if (Array.isArray(listing)) {
        const upsert = makeUpsert();
        listing.forEach(upsert);
        set(searchResultsAtom, { ...data, events, listings });
      } else if ('listingsToUpdate' in listing) {
        if ((listing.listingsToRemove?.length ?? 0) > 0) {
          const listingsToRemove = listings.filter((l) => listing.listingsToRemove!.some((r) => r.tenantId === l.tenantId && r.listingId === l.listingId));
          listingsToRemove.forEach((l) => {
            removeListing(l, listings, events);
          });
        }
        // Build the index map after removals so it reflects the spliced array.
        const upsert = makeUpsert();
        listing.listingsToUpdate.forEach(upsert);
        set(searchResultsAtom, { ...data, events, listings });
        if (listing.newSelectedListingId) {
          set(selectedTenantListingIdAtom, listing.newSelectedListingId);
        }
      } else {
        makeUpsert()(listing);
        set(searchResultsAtom, { ...data, events, listings });
      }
    }
  },
);

export const showIgnoredListingsAtom = atom(false);
export const showOwnListingsAtom = atom(false);
export const showOutliersAtom = atom(false);
export const autoUpdateRuleOnTargetComparableChangesAtom = atom(false);

export const targetComparableFloorPriceRuleAtom = atom((get) => {
  const auto = get(autoUpdateRuleOnTargetComparableChangesAtom);
  const rule = get(ruleStateAtom);
  const selectedListing = get(selectedMergedListingAtom);
  const minFloorPrice = get(auth.tenantsAtom).filter((t) => t.tenantId === selectedListing?.tenantId)[0]?.settings?.pricerSettings?.minimumFloorPrice ?? 0;
  if (auto && rule.automationTypeId !== 'SchedulePrice') {
    const settings = get(auth.principalAtom)?.settings;
    const floorMultiplier = settings?.pricerSettings?.floorMultiplier || 0;
    const ceilingMultiplier = settings?.pricerSettings?.ceilingMultiplier || 0;
    const floorCostMultiplier = settings?.pricerSettings?.floorCostMultiplier || 0;
    const ceilingCostMultiplier = settings?.pricerSettings?.ceilingCostMultiplier || 0;
    const targetComparables = get(targetComparablesAtom);
    const costBasedFloor = (selectedListing?.unitCost ?? 0) * floorCostMultiplier;
    const costBasedCeiling = (selectedListing?.unitCost ?? 0) * ceilingCostMultiplier;
    let compBasedFloor = 0;
    let compBasedCeiling = 0;

    if (targetComparables && targetComparables.length > 0) {
      const targetComparablePrice = GetTargetComparablePrice(targetComparables);
      if (targetComparablePrice && floorMultiplier) {
        compBasedFloor = parseFloat((targetComparablePrice * floorMultiplier).toFixed(2));
      }

      if (targetComparablePrice && ceilingMultiplier) {
        compBasedCeiling = parseFloat((targetComparablePrice * ceilingMultiplier).toFixed(2));
      }
    }

    let calculatedFloor = Math.max(costBasedFloor, compBasedFloor);
    const calculatedCeiling = Math.max(costBasedCeiling, compBasedCeiling);

    if (calculatedFloor > 0 && minFloorPrice && minFloorPrice > calculatedFloor) {
      calculatedFloor = minFloorPrice;
    }

    if (calculatedFloor === 0) {
      rule.floorPrice = undefined;
    } else {
      rule.floorPrice = calculatedFloor;
    }

    if (calculatedCeiling === 0) {
      rule.ceilingPrice = undefined;
    } else {
      rule.ceilingPrice = calculatedCeiling;
    }
    return rule;
  }
  return rule;
});

export const _marketplaceListingsFilteredAtom = atom(async (get) => {
  const selectedMarketplaceEvent = get(selectedMarketplaceEventAtom);
  const seatingChartFilters = get(seatingChartFiltersAtom);
  const listings = get(marketplaceListingsAtom);
  // Raw listings are fully determined by this key, so the worker can reuse one
  // resident copy across array instances. Safe here only because the input is the
  // unfiltered marketplaceListingsAtom — never pass a key for derived/mapped lists.
  const datasetKey = get(marketplaceListingsDatasetKeyAtom);
  const showOwnListings = get(showOwnListingsAtom);
  const showIgnoredListings = get(showIgnoredListingsAtom);
  const showOutliers = get(showOutliersAtom);
  // Precompute the set of (lowercased) mapped sectionIds once instead of rebuilding a lowercased
  // array for every section on every listing.
  const mappedSectionIds = new Set<string>();
  for (const s of selectedMarketplaceEvent.sections ?? []) {
    for (const sectionId of s.sections ?? []) {
      mappedSectionIds.add(sectionId.toLowerCase());
    }
  }
  return (await applyFiltersAsync(listings, seatingChartFilters, showOwnListings, showIgnoredListings, showOutliers, datasetKey ?? undefined)).map((listing) => {
    if (!mappedSectionIds.has(listing.sectionId!.toLowerCase())) {
      return listing;
    }

    return {
      ...listing,
      sectionAlias: selectedMarketplaceEvent?.marketplaceId === 'Ticketmaster' ? listing.section.toUpperCase() : undefined,
    } satisfies MarketplacesListingWithAlias;
  }) as MarketplacesListingWithAlias[];
});

export const marketplaceListingsFilteredAtom = unwrap(_marketplaceListingsFilteredAtom, (prev) => prev ?? []);

export const marketplaceOutlierThresholdAtom = atom((get) => {
  const filters = get(seatingChartFiltersAtom);
  const rowData = get(marketplaceListingsFilteredAtom);

  if (deepEqual(filters, DEFAULT_FILTERS)) {
    return null;
  }

  if (filters.outlierCriteria === undefined) {
    return null;
  }

  // Don't include own listings in outlier calculation
  const notOurListings = rowData?.filter((listing) => !listing.isOwnListing) || [];

  return getOutlierPriceThreshold(notOurListings, filters.outlierCriteria);
});

export const marketplaceListingsOutliersAtom = atom((get) => {
  const filters = get(seatingChartFiltersAtom);
  const rowData = get(marketplaceListingsFilteredAtom);

  if (deepEqual(filters, DEFAULT_FILTERS)) {
    return [];
  }

  if (filters.outlierCriteria === undefined) {
    return [];
  }

  return getOutlierListings(rowData || [], filters.outlierCriteria);
});

export const marketplaceTargetComparablesAtom = atom((get) => {
  const filters = get(seatingChartFiltersAtom);
  let rowData = get(marketplaceListingsFilteredAtom);
  const { isAutoPriced, numComparables } = get(ruleStateAtom);

  if (!isAutoPriced) {
    return null;
  }

  if (deepEqual(filters, DEFAULT_FILTERS)) {
    return null;
  }

  // Exclude own listings
  rowData = rowData?.filter((listing) => !listing.isOwnListing);

  // If there are no primary filters then return nothing
  if (!((filters?.sectionIds && filters?.sectionIds?.length > 0) || filters?.rows !== '' || (filters?.splits && filters?.splits?.length > 0))) {
    return null;
  }

  if (filters.outlierCriteria) {
    const outliers = new Set(get(marketplaceListingsOutliersAtom));
    rowData = rowData?.filter((listing) => !outliers.has(listing));
  }

  if (filters.ignoredListingIds && filters.ignoredListingIds?.length > 0) {
    const ignoredListingIds = new Set(filters.ignoredListingIds);
    const filtered = rowData?.filter((listing) => !ignoredListingIds.has(listing.listingId)) || [];
    return GetTargetComparables(filtered, numComparables ?? 1) ?? null;
  }

  return GetTargetComparables(rowData ?? [], numComparables ?? 1) ?? null;
});

export const marketplaceTargetComparablesTransientAtom = atom((get) => {
  const filters = get(seatingChartFiltersAtom);
  let rowData = get(marketplaceListingsFilteredAtom);
  const { numComparables } = get(ruleStateAtom);

  // Exclude own listings
  rowData = rowData?.filter((listing) => !listing.isOwnListing);

  // If there are no primary filters then return nothing
  if (!((filters?.sectionIds && filters?.sectionIds?.length > 0) || filters?.rows !== '' || (filters?.splits && filters?.splits?.length > 0))) {
    return null;
  }

  if (filters.outlierCriteria) {
    const outliers = new Set(get(marketplaceListingsOutliersAtom));
    rowData = rowData?.filter((listing) => !outliers.has(listing));
  }

  if (filters.ignoredListingIds && filters.ignoredListingIds?.length > 0) {
    const ignoredListingIds = new Set(filters.ignoredListingIds);
    const filtered = rowData?.filter((listing) => !ignoredListingIds.has(listing.listingId)) || [];
    const target = GetTargetComparables(filtered, numComparables ?? 1) ?? null;
    return target;
  }

  const target = GetTargetComparables(rowData ?? [], numComparables ?? 1) ?? null;
  return target;
});

export const targetComparablesAtom = atom((get) => get(marketplaceTargetComparablesAtom));

// Per-card override inputs for the market-listings fetch. Season cards render one
// MarketListingProvider per event and pass the event as props; these scoped atoms
// carry that override into atom-land so the query can be fully derived. Three
// states, mirroring the hook's `override !== undefined ? override : global` logic:
// `undefined` = no override => fall back to the global selection; `null` = override
// present but empty => no event (query disabled); object = use the override. Only
// the two scalar fields are stored, to sidestep the override (InventoryEventMapping)
// vs global (MarketplaceEventExtended) type mismatch. Set by MarketListingState.
export const overrideMarketplaceEventAtom = atom<
  { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace | null | undefined; marketplaceEventId: string | null | undefined } | null | undefined
>(undefined);
export const overrideEventAtom = atom<BrokerNerdsBarkerCoreModelsInventoryEvent | null | undefined>(undefined);
// Mirrors MarketListingState's `disabled` prop so the query's `enabled` matches
// today's gating (e.g. non-default season-card variants don't fetch). Defaults to
// `true` so the query stays disabled until a mounted provider's effect opts in:
// without this, the one-render window before that effect runs would let every
// card's query fire prematurely (notably while isLoading, when all cards should be
// gated), flooding the fetch limiter — which showed up as a negative loading % in
// the season pricer.
export const marketListingsDisabledAtom = atom(true);

// The tenant/marketplace/event that actually drive the fetch: the override when a
// season card supplied one (override !== undefined), otherwise the global selection.
export const effectiveListingsParamsAtom = atom((get) => {
  const mpOverride = get(overrideMarketplaceEventAtom);
  const mp = mpOverride !== undefined ? mpOverride : get(selectedMarketplaceEventAtom);
  const eventOverride = get(overrideEventAtom);
  const event = eventOverride !== undefined ? eventOverride : get(selectedEventAtom);
  return {
    marketplaceId: mp?.marketplaceId ?? undefined,
    marketplaceEventId: mp?.marketplaceEventId ?? undefined,
    tenantId: event?.tenantId ?? undefined,
  };
});

// Single source of truth for raw market listings. Shares the app QueryClient (see
// api-config's queryClientAtom hydration) and the hook's query key (tenantId
// appended) so there's one cache entry, observed by both this atom and
// fetchUpdatedListings' setQueryData. No `select`: the full Axios response is kept
// so the staleness effect in MarketListingState can still read response headers.
export const marketplaceListingsQueryAtom = atomWithQuery((get) => {
  const { marketplaceId, marketplaceEventId, tenantId } = get(effectiveListingsParamsAtom);
  return {
    // Same key the hook used (base key + tenantId) so this atom and
    // fetchUpdatedListings' setQueryData hit one shared cache entry.
    queryKey: [...getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey(marketplaceId!, marketplaceEventId!, { bypassCache: false }), tenantId],
    // Wrapped in marketListingsLimit so /seasons (one query per event) can't flood
    // the API — preserves the hook's original throttling.
    queryFn: ({ signal }) =>
      marketListingsLimit(() =>
        getApiMarketplacesMarketplaceIdEventsEventIdListings(marketplaceId!, marketplaceEventId!, { bypassCache: false }, { signal, headers: { 'x-tenant-id': tenantId } }),
      ),
    enabled: !!tenantId && !!marketplaceId && !!marketplaceEventId && !get(marketListingsDisabledAtom),
    staleTime: 60 * 1000,
    gcTime: 60 * 1000,
  };
});

// Stable empty result so the derived atom doesn't churn a fresh [] on every query
// observer notification (isFetching toggles etc.) while data is absent.
const EMPTY_LISTINGS: BrokerNerdsBarkerCoreModelsMarketplacesListing[] = [];

// Raw listings + their composite identity now DERIVE from the query — no imperative
// push, no requestAnimationFrame. The off-thread filter (marketplaceListingsFilteredAtom)
// and the worker keyed-reuse read these unchanged.
export const marketplaceListingsAtom = atom((get) => get(marketplaceListingsQueryAtom).data?.data?.listings ?? EMPTY_LISTINGS);

// Composite identity (tenant|marketplace|event|asOfDate) of the raw listings above,
// derived from the same query result. asOfDate is the data version, so new data
// yields a new key; null when any part is missing => filter's safe per-instance path.
export const marketplaceListingsDatasetKeyAtom = atom<string | null>((get) => {
  const response = get(marketplaceListingsQueryAtom).data?.data;
  const { marketplaceId, marketplaceEventId, tenantId } = get(effectiveListingsParamsAtom);
  return response?.asOfDate && tenantId && marketplaceId && marketplaceEventId
    ? `${tenantId}|${marketplaceId}|${marketplaceEventId}|${new Date(response.asOfDate).getTime()}`
    : null;
});

const _selectedMarketplaceIdAtom = atom<BrokerNerdsBarkerCoreEnumsMarketplace | undefined>(undefined);
export const selectedMarketplaceIdAtom = atom(
  (get) => get(_selectedMarketplaceIdAtom),
  (get, set, marketplace: BrokerNerdsBarkerCoreEnumsMarketplace) => {
    const rule = get(ruleStateAtom);
    if (rule && rule.marketplaceId !== marketplace) {
      set(ruleStateAtom, {
        ...rule,
        marketplaceId: marketplace,
        filters: { ...rule.filters, sectionIds: [] },
      });
    }
    set(_selectedMarketplaceIdAtom, marketplace);
  },
);

export const selectedMarketplaceEventAtom = atom<MarketplaceEventExtended>({
  marketplaceEventId: null,
  marketplaceId: null,
  svgDetails: null,
  marketplaceEvent: null,
  isUserMapped: false,
  metadata: null,
  sections: [],
  isLoading: false,
});

export const showEventMappingAtom = atom<boolean>(false);

const _showEventPerformanceAtom = atom<boolean>(false);
export const showEventPerformanceAtom = atom(
  (get) => get(_showEventPerformanceAtom),
  (get, set, value: boolean) => {
    set(_showEventPerformanceAtom, value);
  },
);

export const selectedSaleAtom = atom<Sale | undefined>(undefined);

export const overrideSelectedMergedListingAtom = atom<BarkerEventListing | undefined>(undefined);
export const selectedMergedListingAtom = atom((get) => {
  if (!get(overrideSelectedMergedListingAtom)) {
    const { tenantId, listingId } = get(selectedTenantListingIdAtom);
    const inventory = get(mergedEventListingsAtom);
    return inventory?.find((listing) => listing.tenantId === tenantId && listing.listingId === listingId);
  }
  return get(overrideSelectedMergedListingAtom);
});

export const selectedSectionAtom = atom<string | undefined>(undefined);
export const selectedListingRuleIdAtom = atom<string | undefined>(undefined);

export const selectedMergedListingLastRuleTierAtom = atom((get) => {
  const selected = get(selectedMergedListingAtom);
  const inventory = get(mergedEventListingsUnfilteredRulesAtom);
  const listingsMatchingRuleId = inventory?.filter((l) => l.ruleId === selected?.ruleId);

  // Return largest tier
  return listingsMatchingRuleId?.reduce((prev, listing) => (prev > (listing.ruleTier || 0) ? prev : listing.ruleTier || 0), 0);
});

export const listingsMatchingSelectedRuleIdAtom = atom((get) => {
  const selected = get(selectedMergedListingAtom);
  const inventory = get(mergedEventListingsUnfilteredRulesAtom);
  return inventory?.filter((l) => l.ruleId === selected?.ruleId);
});

export const selectedMergedListingPriceAtom = atom((get) => get(selectedMergedListingAtom)?.unitPrice);

const _selectedTenantListingIdAtom = atom<StringWithPipe | null | undefined>(null);
export const previousSelectedTenantListingIdAtom = atom<StringWithPipe | null | undefined>(null);

export const selectedTenantListingIdAtom = atom(
  (get) => new TenantIdListingId(get(_selectedTenantListingIdAtom)),
  (get, set, input: StringWithPipe | null | undefined, options: { keepRule: boolean } = { keepRule: false }) => {
    const current = get(_selectedTenantListingIdAtom);
    if (current !== input) {
      const inventory = get(mergedEventListingsAtom);
      const [tenantId, listingId] = input?.split('|') || [];
      const inventoryListing = inventory?.find((listing) => listing.tenantId === tenantId && listing.listingId === listingId);
      if (current === null) {
        const currentEvent = get(_selectedEventAtom);
        if (currentEvent?.eventId) {
          const currentEventKey = `${currentEvent.tenantId}|${currentEvent.eventId}`;
          set(ruleFiltersByEventIdAtom, (s) => ({ ...s, [currentEventKey]: get(ruleStateAtom)?.filters }));
        }
      }
      set(previousSelectedTenantListingIdAtom, current);
      set(_selectedTenantListingIdAtom, input);
      set(selectedEventAtom, inventoryListing?.event);
      set(selectedSectionAtom, inventoryListing?.section);
      set(initialPageLoadAtom, false);
      if (!inventoryListing?.ruleId && !options.keepRule) {
        // Note: This is when we restart rule if there isn't one. First entry point to mix with principal defaults
        set(ruleStateAtom, null);
      }
    }

    if (input !== null) {
      if (get(showEventPerformanceAtom)) {
        set(showEventPerformanceAtom, false);
      }
    }
  },
);

const _selectedEventAtom = atom<BrokerNerdsBarkerCoreModelsInventoryEvent | null | undefined>(null);
export const previousSelectedEventAtom = atom<BrokerNerdsBarkerCoreModelsInventoryEvent | null | undefined>(null);

export const selectedEventAtom = atom(
  (get) => get(_selectedEventAtom),
  (get, set, event: BrokerNerdsBarkerCoreModelsInventoryEvent | null | undefined) => {
    const currentEvent = get(_selectedEventAtom);
    if ((currentEvent?.tenantId ?? '') + (currentEvent?.eventId ?? '') !== (event?.tenantId ?? '') + (event?.eventId ?? '')) {
      set(previousSelectedEventAtom, currentEvent);
    }
    set(_selectedEventAtom, event);
    const filterMode = get(filterModeAtom);
    const hasSelectedListing = get(selectedTenantListingIdAtom).hasValue();
    if (!hasSelectedListing && event?.eventId) {
      if (filterMode === FilterMode.Locked) {
        const lockedEventRuleFilters = get(ruleFiltersByEventIdAtom)[`${event.tenantId}|${event.eventId}`];
        const principalPricerSettings = get(auth.principalAtom)?.settings?.pricerSettings;
        const availableMarketplaces = get(auth.tenantsAtom).find((t) => t.tenantId === event.tenantId)?.marketplaceIds;
        const defaultMarketplace = getDefaultMarketplaceForTenant(availableMarketplaces, principalPricerSettings?.defaultMarketplace);
        set(ruleStateAtom, {
          ...DEFAULT_RULE,
          ruleId: uuid(),
          marketplaceId: defaultMarketplace,
          filters: lockedEventRuleFilters ? structuredClone(lockedEventRuleFilters) : { ...DEFAULT_RULE.filters },
          autoAdjustSplits: false,
        });
      } else {
        set(ruleStateAtom, null);
      }
    }
  },
);

export const pendingListingUpdatesAtom = atom<ListingUpdate[]>([]);

export const pendingListingUpdatesByListingIdAtom = atom(
  (get) => {
    const pending = get(pendingListingUpdatesAtom);
    return pending.reduce(
      (acc, update) => {
        const key = `${update.tenantId}|${update.listingId}`;
        if (acc[key]) {
          acc[key].push(update);
        } else {
          acc[key] = [update];
        }
        return acc;
      },
      {} as { [key: string]: ListingUpdate[] },
    );
  },
  (get, set, update: ListingUpdate) => {
    const { tenantId, listingId, property } = update;
    const pending = get(pendingListingUpdatesAtom);
    const filtered = pending.filter((x) => !(x.tenantId === tenantId && x.listingId === listingId && x.property === property));
    filtered.push(update);
    set(pendingListingUpdatesAtom, filtered);
  },
);

/**
 * Per-listing slice of {@link pendingListingUpdatesAtom}, scoped by `${tenantId}|${listingId}`.
 *
 * Inventory cell renderers (price, broadcasting) used to read pending updates through
 * `pendingListingUpdatesByProperty`, a useCallback keyed on the whole pending-updates array. Its
 * identity changed on every pending change for any listing and — because each yasml value is its
 * own React context — every mounted price/broadcasting cell re-rendered whenever a single listing
 * was staged. Subscribing to this atom instead (deepEqual selector) re-renders a row only when that
 * row's own pending updates actually change.
 *
 * Derives from {@link pendingListingUpdatesByListingIdAtom} (keyed by the same `${tenantId}|${listingId}`)
 * so the grouping happens once per pending change, shared across all rows, rather than each mounted row
 * re-filtering the whole pending array.
 */
export const pendingUpdatesForListingAtom = atomFamily((tenantIdListingId: string) =>
  selectAtom(pendingListingUpdatesByListingIdAtom, (byListingId) => byListingId[tenantIdListingId] ?? [], deepEqual),
);

/** Latest pending update for a property within a listing's slice (preserves prior last-wins semantics). */
export const latestPendingUpdate = (updates: ListingUpdate[], property: ListingUpdate['property']) => {
  for (let i = updates.length - 1; i >= 0; i -= 1) {
    if (updates[i].property === property) {
      return updates[i];
    }
  }
  return null;
};

export const applyRuleToSelectedGroupedListingsAtom = atom(null, (get, set, listPrice?: number) => {
  const selectedListing = get(selectedMergedListingAtom);
  const ruleState = get(ruleStateAtom);
  const cleanRuleState = get(ruleStateInitialValueAtom);
  const { numActive, isAutoPriced, automationTypeId } = ruleState;
  const tenants = get(auth.tenantsAtom);
  const roundingSettings = tenants.filter((t) => t.tenantId === selectedListing?.tenantId)[0]?.settings?.pricerSettings?.roundPrices ?? 'None';

  if (!selectedListing || (!isAutoPriced && automationTypeId !== 'SchedulePrice') || selectedListing.ruleId !== ruleState.ruleId) {
    return;
  }

  const listingWithMatchingRuleId = get(mergedEventListingsUnfilteredRulesAtom)
    ?.filter((listing) => selectedListing.ruleId != null && selectedListing.ruleId === listing.ruleId)
    .sort((a, b) => a.ruleTier! - b.ruleTier!);

  set(transientGlobalStateAtom, (s) => ({ ...s, applyGroupListingRule: false }));

  // Invariant across the loop (nothing set inside the loop feeds these), so read once instead of per listing.
  const targetComparables = get(targetComparablesAtom);
  const filtersEmpty = get(filtersEmptyAtom);
  const principalPricerSettings = get(auth.principalAtom)?.settings?.pricerSettings;

  let anchorPrice = automationTypeId === 'SchedulePrice' && listPrice ? listPrice : 0;
  listingWithMatchingRuleId?.forEach((listing, i) => {
    const price = calculateTargetPrice({
      listing,
      offset: i,
      anchorPrice,
      rule: ruleState,
      targetComparables,
      tenantRoundingSettings: roundingSettings,
    });
    if (i === 0) {
      anchorPrice = price;
    }

    if (!filtersEmpty && !ruleState.pausedAt) {
      if (price !== listing.unitPrice) {
        const listingUpdate: ListingUpdate = {
          listingId: listing.listingId,
          tenantId: listing.tenantId,
          property: 'unitPrice',
          value: price,
          previousValue: listing.unitPrice,
        };
        set(pendingListingUpdatesByListingIdAtom, listingUpdate);
      } else {
        const existingUpdate = get(pendingListingUpdatesAtom).find((x) => x.tenantId === listing.tenantId && x.listingId === listing.listingId && x.property === 'unitPrice');
        if (existingUpdate) {
          const filtered = get(pendingListingUpdatesAtom).filter((x) => !(x.tenantId === listing.tenantId && x.listingId === listing.listingId && x.property === 'unitPrice'));
          set(pendingListingUpdatesAtom, filtered);
        }
      }
    }

    if (listing.pricerStatusId === 'None') {
      let pricerStatusId: BrokerNerdsBarkerCoreEnumsPricerStatus = 'None';
      if (ruleState.isAutoPriced) {
        if (ruleState.floorPrice && price <= ruleState.floorPrice) {
          pricerStatusId = 'AtFloor';
        }
        if (ruleState.ceilingPrice && price >= ruleState.ceilingPrice && i === 0) {
          pricerStatusId = 'AtCeiling';
        }
        pricerStatusId = 'AutoPriced';
      } else if (ruleState.automationTypeId === 'SchedulePrice') {
        if (ruleState.floorPrice && price <= ruleState.floorPrice) {
          pricerStatusId = 'AtFloor';
        }
        if (ruleState.ceilingPrice && price >= ruleState.ceilingPrice && i === 0) {
          pricerStatusId = 'AtCeiling';
        }
        pricerStatusId = 'Scheduled';
      }
      const listingUpdate: ListingUpdate = {
        listingId: listing.listingId,
        tenantId: listing.tenantId,
        property: 'pricerStatusId',
        value: pricerStatusId,
        previousValue: listing.pricerStatusId,
      };
      set(pendingListingUpdatesByListingIdAtom, listingUpdate);
    }

    if (numActive != null && listingWithMatchingRuleId.length > 1) {
      const active = i < numActive;
      if (active && !listing.splits) {
        const tenantPointOfSale = tenants.find((t) => t.tenantId === listing?.event?.tenantId)?.pointOfSaleId;
        if (tenantPointOfSale) {
          const splitUpdate = convertSplitsModelToPointOfSaleModel(tenantPointOfSale, getPointOfSaleSplitType(tenantPointOfSale, listing?.splits));
          if (splitUpdate) {
            const pendingSplitUpdate: ListingUpdate = {
              listingId: listing.listingId,
              tenantId: listing.tenantId,
              property: 'splits',
              value: splitUpdate,
              previousValue: listing.splits,
            };
            set(pendingListingUpdatesByListingIdAtom, pendingSplitUpdate);
          }
        }
      } else {
        const filtered = get(pendingListingUpdatesAtom).filter((x) => !(x.tenantId === listing.tenantId && x.listingId === listing.listingId && x.property === 'splits'));
        set(pendingListingUpdatesAtom, filtered);
      }
    }

    if (
      numActive != null &&
      (listingWithMatchingRuleId.length > 1 ||
        (!cleanRuleState?.isAutoPriced && ruleState.isAutoPriced && (principalPricerSettings?.broadcastSingleListing ?? DEFAULT_RULE_SETTINGS.broadcastSingleListing)))
    ) {
      const active = i < numActive;
      if (listing.isBroadcasting !== active) {
        const activeUpdate: ListingUpdate = {
          tenantId: listing.tenantId,
          listingId: listing.listingId,
          property: 'isBroadcasting',
          value: active,
          previousValue: listing.isBroadcasting,
        };

        set(pendingListingUpdatesByListingIdAtom, activeUpdate);

        const pointOfSaleId = tenants.find((t) => t.tenantId === listing.tenantId)?.pointOfSaleId;

        if (pointOfSaleId === 'DtiPortal' && (listing.splits as BrokerNerdsBarkerCoreModelsInventoryDtiSplitRules).splitType.toLowerCase() === 'none') {
          const splitsUpdate: ListingUpdate = {
            tenantId: listing.tenantId,
            listingId: listing.listingId,
            property: 'splits',
            value: { splitType: 'DontLeaveOne' },
            previousValue: listing.splits,
          };

          set(pendingListingUpdatesByListingIdAtom, splitsUpdate);
        }
      } else {
        const filtered = get(pendingListingUpdatesAtom).filter((x) => !(x.tenantId === listing.tenantId && x.listingId === listing.listingId && x.property === 'isBroadcasting'));
        set(pendingListingUpdatesAtom, filtered);
      }
    }
  });
});

export const rejectAllPendingUpdatesAtom = atom(null, (get, set, tenantIdListingIds?: StringWithPipe | StringWithPipe[]) => {
  const pendingListingUpdates = get(pendingListingUpdatesAtom);
  const searchResults = get(searchResultsAtom);
  if (pendingListingUpdates.length > 0) {
    // Keys are the composite `${tenantId}|${listingId}` so listings with the same listingId across tenants aren't conflated.
    const keyFilter = tenantIdListingIds ? new Set<string>(Array.isArray(tenantIdListingIds) ? tenantIdListingIds : [tenantIdListingIds]) : null;
    const matchesFilter = (x: ListingUpdate) => (keyFilter ? keyFilter.has(`${x.tenantId}|${x.listingId}`) : true);
    const groupedUpdates = pendingListingUpdates.filter(matchesFilter).reduce(
      (acc, update) => {
        const key = `${update.tenantId}|${update.listingId}`;
        if (acc[key]) {
          acc[key].push(update);
        } else {
          acc[key] = [update];
        }
        return acc;
      },
      {} as { [key: string]: ListingUpdate[] },
    );
    const listingByKey = new Map(searchResults?.listings.map((l) => [`${l.tenantId}|${l.listingId}`, l]));
    const updatedListings = Object.entries(groupedUpdates).map(([key, grouped]) => {
      const updates = grouped.reverse();
      const listing = structuredClone(listingByKey.get(key));
      if (listing) {
        updates.forEach((update) => {
          (listing[update.property] as unknown) = update.previousValue;
        });
      }
      return listing;
    });
    set(updateListingsAtom, updatedListings.filter(Boolean));
    set(
      pendingListingUpdatesAtom,
      pendingListingUpdates.filter((x) => (keyFilter ? !keyFilter.has(`${x.tenantId}|${x.listingId}`) : false)),
    );
  }
});

export const rejectPendingUpdatesByListingIdAtom = atom(null, (get, set, tenantIdListingId: StringWithPipe) => {
  const { tenantId, listingId } = TenantIdListingId.fromString(tenantIdListingId);
  const pendingListingUpdates = get(pendingListingUpdatesAtom).filter((x) => x.tenantId === tenantId && x.listingId === listingId);
  const searchResults = get(searchResultsAtom);
  const listing = structuredClone(searchResults?.listings.find((l) => l.tenantId === tenantId && l.listingId === listingId));
  if (pendingListingUpdates.length > 0 && listing) {
    pendingListingUpdates.forEach((update) => {
      // @ts-ignore
      listing[update.property] = update.previousValue;
    });
    set(updateListingsAtom, listing);
    set(pendingListingUpdatesAtom, (prev) => prev.filter((x) => !(x.tenantId === tenantId && x.listingId === listingId)));
  }
});

export const clearPendingUpdateByPropertyAtom = atom(
  null,
  (
    _,
    set,
    {
      tenantId,
      listingId,
      properties,
    }: {
      tenantId: string;
      listingId: string;
      properties: (keyof BrokerNerdsBarkerCoreModelsInventoryListing)[];
    },
  ) => {
    set(pendingListingUpdatesAtom, (prev) => prev.filter((update) => !(update.tenantId === tenantId && update.listingId === listingId && properties.includes(update.property))));
  },
);

const _eventMarketplaceDefaultsAtom = atom<Record<string, BrokerNerdsBarkerCoreEnumsMarketplace>>({});
export const eventMarketplaceDefaultsAtom = atom(
  (get) => get(_eventMarketplaceDefaultsAtom),
  (get, set, { marketplaceId, tenantId, eventId }: { marketplaceId: BrokerNerdsBarkerCoreEnumsMarketplace; tenantId: string; eventId: string }) => {
    const defaults = get(_eventMarketplaceDefaultsAtom);
    set(_eventMarketplaceDefaultsAtom, { ...defaults, [`${tenantId}|${eventId}`]: marketplaceId });
  },
);

export const localRuleDefaultAtom = atom<Omit<Rule, 'ruleId'>>(() => DEFAULT_RULE);

export const ruleStateQueryAtom = atomWithQuery((get) =>
  getGetApiPricingRulesRuleIdQueryOptions(get(selectedMergedListingAtom)?.ruleId!, {
    query: {
      select(data) {
        return data.data;
      },
      enabled: false,
    },
    axios: {
      headers: {
        'x-tenant-id': get(selectedMergedListingAtom)?.event?.tenantId,
      },
    },
  }),
);

// store.sub(ruleStateQueryAtom, () => {
//   const serverRule = store.get(ruleStateQueryAtom);
//   if (serverRule.data && serverRule.data.ruleId !== '00000000-0000-0000-0000-000000000000') {
//     store.set(ruleStateAtom, serverRule.data!);
//   }
// });

export const filterModeAtom = atom(FilterMode.Standard);

const _PAGE_LOAD_RULE_ID = uuid();

const _ruleStateSharedAtom = atom<{ base: Rule; rule: Rule }>({
  base: { ...DEFAULT_RULE, ruleId: _PAGE_LOAD_RULE_ID },
  rule: { ...DEFAULT_RULE, ruleId: _PAGE_LOAD_RULE_ID },
});
export const ruleStateInitialValueAtom = focusAtom(_ruleStateSharedAtom, (a) => a.prop('base'));
const _ruleStateAtom = focusAtom(_ruleStateSharedAtom, (a) => a.prop('rule'));

export const initiallyAutoPricedAtom = atom((get) => {
  const rule = get(ruleStateInitialValueAtom);
  return !!(rule?.isAutoPriced || rule?.automationTypeId === 'SchedulePrice');
});

export const ruleStateResetAtom = atom(null, (get, set, options?: { retainFilters: boolean }) => {
  if (options && options.retainFilters) {
    const filters = get(_ruleStateAtom)!.filters!;
    const rule = get(ruleStateInitialValueAtom)!;
    set(_ruleStateAtom, { ...rule, filters });
  } else {
    set(_ruleStateAtom, get(ruleStateInitialValueAtom));
  }
});

export const ruleFiltersByEventIdAtom = atom({} as Record<string, Rule['filters']>);

// Used for winventory. Will be set in the querystring
export const forceRuleIdAtom = atom<string | null>(null);

export const ruleStateAtom = atom(
  (get) => get(_ruleStateAtom),
  (
    get,
    set,
    rule: Rule | string | null | SetStateAction<Rule>,
    options?: {
      isLocalRule?: boolean;
      isAutoPriced?: boolean;
      forceUpdate?: boolean;
      currentListingId?: string;
      isRuleTemplate?: boolean;
      enableAutoPricing?: 'Auto-Pricing' | 'Scheduled';
    },
  ) => {
    const selectedListing = get(selectedMergedListingAtom);
    const selectedListingRuleId = selectedListing?.ruleId;
    const selectedEvent = get(selectedEventAtom);
    const principalPricerSettings = get(auth.principalAtom)?.settings?.pricerSettings;
    const currentRule = get(ruleStateAtom);
    const selectedTenantId = selectedListing?.tenantId ?? selectedEvent?.tenantId;
    const selectedEventId = selectedListing?.eventId ?? selectedEvent?.eventId;
    const availableMarketplaces = get(auth.tenantsAtom).find((x) => x.tenantId === selectedTenantId)?.marketplaceIds;
    const defaultMarketplace = getDefaultMarketplaceForTenant(availableMarketplaces, principalPricerSettings?.defaultMarketplace);
    const tenantPricerSettings = get(auth.tenantsAtom).filter((t) => t.tenantId === selectedTenantId)[0]?.settings?.pricerSettings;
    if (rule && typeof rule === 'object') {
      if (options && options.forceUpdate && options.currentListingId !== undefined && selectedListing?.tenantIdListingId !== options.currentListingId) {
        // Selected listing changed. Don't update current state
        return;
      }

      const { applyGroupListingRule } = get(transientGlobalStateAtom);
      const copyOfDefaultRule = structuredClone(DEFAULT_RULE);

      const partialScheduledRuleSettingsDefaults: Partial<Rule> = {
        marketplaceId: defaultMarketplace,
        floorPrice:
          principalPricerSettings?.schedulePricerSettings?.floorMultiplier && selectedListing?.unitCost
            ? selectedListing.unitCost * principalPricerSettings.schedulePricerSettings.floorMultiplier
            : copyOfDefaultRule.floorPrice,
        ceilingPrice:
          principalPricerSettings?.schedulePricerSettings?.ceilingMultiplier && selectedListing?.unitCost
            ? selectedListing.unitCost * principalPricerSettings.schedulePricerSettings.ceilingMultiplier
            : copyOfDefaultRule.ceilingPrice,
        numActive: principalPricerSettings?.schedulePricerSettings?.numActive ?? copyOfDefaultRule.numActive,
        staggerByTypeId: principalPricerSettings?.schedulePricerSettings?.staggerByTypeId ?? copyOfDefaultRule.staggerByTypeId,
        adjustmentTypeId: principalPricerSettings?.schedulePricerSettings?.adjustmentTypeId ?? copyOfDefaultRule.adjustmentTypeId,
        staggerByValue: principalPricerSettings?.schedulePricerSettings?.staggerByValue ?? copyOfDefaultRule.staggerByValue,
        schedulePricerSettings: {
          expiresAt: dayjs(selectedListing?.event.localDateTime).add(1, 'day').toDate(),
          intervalMinutes: principalPricerSettings?.schedulePricerSettings?.intervalMinutes ?? DEFAULT_RULE_SETTINGS.intervalMinutes,
        },
        adjustmentValue: principalPricerSettings?.schedulePricerSettings?.adjustmentValue ?? copyOfDefaultRule.adjustmentValue,
      };

      const applyAutoPricerDefaults = options?.enableAutoPricing === 'Auto-Pricing';
      const defaultSoftFloor = principalPricerSettings?.softFloor && principalPricerSettings.softFloor.value > 0 ? principalPricerSettings.softFloor : undefined;
      const isAutoPricedRuleSettings: Partial<Rule> = {
        staggerByTypeId: rule.staggerByTypeId ?? principalPricerSettings?.staggerByTypeId ?? copyOfDefaultRule.staggerByTypeId,
        adjustmentTypeId: rule.adjustmentTypeId ?? principalPricerSettings?.adjustmentTypeId ?? copyOfDefaultRule.adjustmentTypeId,
        marketplaceId: rule.marketplaceId ?? defaultMarketplace,
        adjustmentValue: rule.adjustmentValue ?? principalPricerSettings?.adjustmentValue ?? copyOfDefaultRule.adjustmentValue,
        numActive: rule.numActive ?? principalPricerSettings?.numActive ?? copyOfDefaultRule.numActive,
        staggerByValue: rule.staggerByValue ?? principalPricerSettings?.staggerByValue ?? copyOfDefaultRule.staggerByValue,
        floorPrice: rule.floorPrice ? rule.floorPrice : copyOfDefaultRule.floorPrice,
        ceilingPrice: rule.ceilingPrice ? rule.ceilingPrice : copyOfDefaultRule.ceilingPrice,
        autoAdjustSplits: rule.autoAdjustSplits ?? principalPricerSettings?.autoAdjustSplits ?? copyOfDefaultRule.autoAdjustSplits,
        numComparables: rule.numComparables ?? principalPricerSettings?.numComparables?.[0]?.numComparables ?? copyOfDefaultRule.numComparables,
        softFloor: applyAutoPricerDefaults ? (rule.softFloor ?? defaultSoftFloor) : rule.softFloor,
        trailingFloor: applyAutoPricerDefaults ? (rule.trailingFloor ?? principalPricerSettings?.trailingFloor) : rule.trailingFloor,
      };

      const ruleSettingsDefaultsToMerge =
        options?.enableAutoPricing === 'Auto-Pricing'
          ? isAutoPricedRuleSettings
          : options?.enableAutoPricing === 'Scheduled'
            ? partialScheduledRuleSettingsDefaults
            : isAutoPricedRuleSettings;

      const newRule = {
        ...rule,
        ...ruleSettingsDefaultsToMerge,
        marketplaceId: rule.marketplaceId ?? defaultMarketplace,
        isAutoPriced: rule.isAutoPriced,
        autoAdjustSplits: rule.autoAdjustSplits ?? copyOfDefaultRule.autoAdjustSplits,
        meta: { ...rule.meta, isLocalRule: options?.isLocalRule !== undefined ? options.isLocalRule : rule.meta?.isLocalRule },
        filters: normalizePricingRuleFilters({
          ...rule.filters,
          exclusions: rule.filters?.exclusions ?? copyOfDefaultRule.filters.exclusions,
          deliveryExclusions: rule.filters?.deliveryExclusions ?? copyOfDefaultRule.filters.deliveryExclusions,
          ignoredListingIds: rule.filters?.ignoredListingIds ?? copyOfDefaultRule.filters.ignoredListingIds,
          maxPrice: rule.filters?.maxPrice ?? copyOfDefaultRule.filters.maxPrice,
          minPrice: rule.filters?.minPrice ?? copyOfDefaultRule.filters.minPrice,
          rows: rule.filters?.rows ?? copyOfDefaultRule.filters.rows,
          sectionIds: rule.filters?.sectionIds ?? copyOfDefaultRule.filters.sectionIds,
          splits: getSplitsValue(
            rule.autoAdjustSplits ?? copyOfDefaultRule.autoAdjustSplits,
            rule.filters?.splits,
            selectedListing?.quantityRemaining,
            selectedListing?.row,
            principalPricerSettings?.quantitySplitMatrix,
            principalPricerSettings?.generalAdmissionSplits,
            tenantPricerSettings?.quantitySplitMatrix,
            tenantPricerSettings?.generalAdmissionSplits,
          ),
          // rule.autoAdjustSplits && companyPricerSettings?.quantitySplitMatrix && selectedListing
          //   ? companyPricerSettings?.quantitySplitMatrix[Math.min(selectedListing.quantityRemaining, 8)]
          //   : rule.filters?.splits ?? DEFAULT_RULE.filters.splits,
        }),
      };

      // Fix adjustmentValue in case of Percentage and value being 0
      if (newRule.adjustmentTypeId === 'Percentage' && !newRule.adjustmentValue) {
        newRule.adjustmentValue = 1;
      }

      if (currentRule?.ruleId !== rule.ruleId || (options?.forceUpdate && selectedListingRuleId === rule.ruleId)) {
        set(autoUpdateRuleOnTargetComparableChangesAtom, !!options?.isRuleTemplate);
        set(_ruleStateSharedAtom, {
          base: newRule,
          // rule: newRule,
          rule: {
            ...newRule,
            isAutoPriced: get(transientGlobalStateAtom).autoUpdateAutoPricedListPriceRuleId === rule.ruleId && !rule.automationTypeId ? true : rule.isAutoPriced,
          },
        });
        if (rule.isAutoPriced) {
          set(autoUpdateRuleOnTargetComparableChangesAtom, false);
        }
        set(selectedMarketplaceIdAtom, newRule.marketplaceId);
      } else {
        set(_ruleStateAtom, newRule);
      }

      if (applyGroupListingRule) {
        set(applyRuleToSelectedGroupedListingsAtom);
        set(transientGlobalStateAtom, (s) => ({ ...s, applyGroupListingRule: false }));
      }
    } else if (typeof rule === 'function') {
      const _rule = rule(get(_ruleStateAtom));
      if (options?.isLocalRule) {
        _rule.meta = { isLocalRule: true };
      }
      set(_ruleStateAtom, { ..._rule, filters: normalizePricingRuleFilters(_rule.filters) });
    } else {
      // Handles string & null conditions
      const eventMarketplace = get(eventMarketplaceDefaultsAtom);
      const copyOfDefaultRule = structuredClone(DEFAULT_RULE);
      const newRule = {
        ...get(localRuleDefaultAtom),
        meta: { isLocalRule: !!selectedListingRuleId },
        ruleId: typeof rule === 'string' ? rule : uuid(),
        isAutoPriced: options?.isAutoPriced || false,
        marketplaceId: eventMarketplace[`${selectedTenantId}|${selectedEventId}`] ?? defaultMarketplace,
        numActive: principalPricerSettings?.numActive ?? copyOfDefaultRule.numActive,
        numComparables: principalPricerSettings?.numComparables?.[0]?.numComparables ?? copyOfDefaultRule.numComparables,
        staggerByTypeId: principalPricerSettings?.staggerByTypeId ?? copyOfDefaultRule.staggerByTypeId,
        adjustmentTypeId: principalPricerSettings?.adjustmentTypeId ?? copyOfDefaultRule.adjustmentTypeId,
        staggerByValue: principalPricerSettings?.staggerByValue ?? copyOfDefaultRule.staggerByValue,
        // TODO: Update with schedule defaults settings
        adjustmentValue: principalPricerSettings?.adjustmentValue ?? copyOfDefaultRule.adjustmentValue,
        autoAdjustSplits: principalPricerSettings?.autoAdjustSplits ?? copyOfDefaultRule.autoAdjustSplits,
        filters: {
          ...copyOfDefaultRule.filters,
          outlierCriteria: principalPricerSettings?.outlierCriteria,
          rows: getRowComparisonValue(selectedListing?.row, principalPricerSettings?.rowComparisons),
          exclusions: principalPricerSettings?.exclusions ?? copyOfDefaultRule.filters.exclusions,
          deliveryExclusions: principalPricerSettings?.deliveryExclusions ?? copyOfDefaultRule.filters.deliveryExclusions,
          splits: getSplitsValue(
            principalPricerSettings?.autoAdjustSplits ?? copyOfDefaultRule.autoAdjustSplits,
            null,
            selectedListing?.quantityRemaining,
            selectedListing?.row,
            principalPricerSettings?.quantitySplitMatrix,
            principalPricerSettings?.generalAdmissionSplits,
            tenantPricerSettings?.quantitySplitMatrix,
            tenantPricerSettings?.generalAdmissionSplits,
          ),
        },
      } satisfies Rule;

      // Fix adjustmentValue in case of Percentage and value being 0
      if (newRule.adjustmentTypeId === 'Percentage' && !newRule.adjustmentValue) {
        newRule.adjustmentValue = 1;
      }

      set(_ruleStateSharedAtom, {
        base: newRule,
        rule: newRule,
      });
      set(selectedMarketplaceIdAtom, newRule.marketplaceId);
      set(autoUpdateRuleOnTargetComparableChangesAtom, true);
    }
  },
);

export const seatingChartFiltersAtom = focusAtom(ruleStateAtom, (a) => a.prop('filters'));
export const sectionRuleFilterAtom = focusAtom(seatingChartFiltersAtom, (a) => a.prop('sectionIds'));
export const resetFiltersAtom = atom(null, (get, set) => {
  const initialValue = get(ruleStateInitialValueAtom);
  set(seatingChartFiltersAtom, initialValue!.filters);
});

export const filtersDirtyAtom = atom((get) => {
  const { filters, marketplaceId } = get(ruleStateAtom);
  const initialValue = get(ruleStateInitialValueAtom)?.filters;

  if (!initialValue) {
    return false;
  }

  return !deepEqual(filters, initialValue) || marketplaceId !== get(ruleStateInitialValueAtom)?.marketplaceId;
});

export const filtersEmptyAtom = atom((get) => {
  const { filters } = get(ruleStateAtom);
  return weakEquals(filters, DEFAULT_FILTERS);
});

export const filtersAtom = atom((get) => {
  const filters = get(ruleStateAtom)?.filters;
  return filters;
});

export const ruleDirtyAtom = atom((get) => {
  const rule = get(ruleStateAtom);
  const initialValue = get(ruleStateInitialValueAtom);
  if (rule && rule.isAutoPriced) {
    return !weakEquals(rule, initialValue, [
      'ruleId',
      'numActive',
      'adjustmentTypeId',
      'marketplaceId',
      'ceilingPrice',
      'floorPrice',
      'adjustmentValue',
      'isAutoPriced',
      'staggerByTypeId',
      'staggerByValue',
      'numComparables',
      'automationTypeId',
      'autoAdjustSplits',
      'softFloor',
      'trailingFloor',
      'pausedAt',
      'pausedBy',
      'warningNotes',
    ]);
  }
  if (rule && rule.automationTypeId === 'SchedulePrice') {
    const ruleDirty = !weakEquals(rule, initialValue, [
      'ruleId',
      'numActive',
      'adjustmentTypeId',
      'marketplaceId',
      'ceilingPrice',
      'floorPrice',
      'adjustmentValue',
      'staggerByTypeId',
      'staggerByValue',
      'automationTypeId',
      'autoAdjustSplits',
      'pausedAt',
      'pausedBy',
      'warningNotes',
    ]);
    const scheduleDirty = !weakEquals(rule.schedulePricerSettings || {}, initialValue?.schedulePricerSettings);
    return ruleDirty || scheduleDirty;
  }
  if (rule && !rule.isAutoPriced) {
    return !weakEquals(rule, initialValue, ['isAutoPriced', 'automationTypeId']);
  }
  return false;
});

export const calculatedTargetPriceAtom = atom((get) => {
  const listing = get(selectedMergedListingAtom);
  if (listing) {
    const rule = get(targetComparableFloorPriceRuleAtom);
    const targetComparables = get(targetComparablesAtom);
    const roundingSettings = get(auth.tenantsAtom).find((t) => t.tenantId === listing.tenantId)?.settings?.pricerSettings?.roundPrices ?? 'None';
    return calculateTargetPrice({ listing, offset: 0, rule, targetComparables, tenantRoundingSettings: roundingSettings });
  }
  return 0;
});

export const transientGlobalStateAtom = atom<GlobalTransientState>({
  autoUpdateAutoPricedListPriceRuleId: null,
  applyGroupListingRule: false,
  ignoreGridScrolling: false,
  expandGroupByListingId: null,
} satisfies GlobalTransientState);

export const mobileVersionAtom = atom('');
